import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Image as ImageIcon, Plus, X, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";

export default function AdminMemoriaEdit() {
  const { memoryId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!memoryId;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaTarget, setMediaTarget] = useState("featured");
  const [mediaList, setMediaList] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content_html: "",
    category: "general",
    year_approx: "",
    location_scope: "choluteca",
    municipio: "",
    featured_image_id: "",
    featured_image_url: "",
    before_image_id: "",
    before_image_url: "",
    after_image_id: "",
    after_image_url: "",
    gallery_image_ids: [],
    gallery_urls: [],
    video_urls: [],
    author_name: "",
    status: "draft",
  });

  useEffect(() => {
    if (isEdit) {
      fetchMemory();
    }
  }, [memoryId]);

  const fetchMemory = async () => {
    try {
      const response = await axios.get(`${API}/memories/${memoryId}`, {
      });
      const memory = response.data;
      setFormData({
        title: memory.title || "",
        slug: memory.slug || "",
        excerpt: memory.excerpt || "",
        content_html: memory.content_html || "",
        category: memory.category || "general",
        year_approx: memory.year_approx || "",
        location_scope: memory.location_scope || "choluteca",
        municipio: memory.municipio || "",
        featured_image_id: memory.featured_image_id || "",
        featured_image_url: memory.featured_image_url || "",
        before_image_id: memory.before_image_id || "",
        before_image_url: memory.before_image_url || "",
        after_image_id: memory.after_image_id || "",
        after_image_url: memory.after_image_url || "",
        gallery_image_ids: memory.gallery_image_ids || [],
        gallery_urls: memory.gallery_urls || [],
        video_urls: memory.video_urls || [],
        author_name: memory.author_name || "",
        status: memory.status || "draft",
      });
    } catch (error) {
      toast.error("Error al cargar la memoria");
      navigate("/admin/memorias");
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media?limit=50`, {
      });
      setMediaList(response.data.items);
    } catch (error) {
      console.error("Error fetching media:", error);
    }
  };

  const handleMediaOpen = (target) => {
    setMediaTarget(target);
    fetchMedia();
    setMediaOpen(true);
  };

  const handleMediaSelect = (media) => {
    if (mediaTarget === "featured") {
      setFormData({
        ...formData,
        featured_image_id: media.media_id,
        featured_image_url: media.url,
      });
    } else if (mediaTarget === "before") {
      setFormData({
        ...formData,
        before_image_id: media.media_id,
        before_image_url: media.url,
      });
    } else if (mediaTarget === "after") {
      setFormData({
        ...formData,
        after_image_id: media.media_id,
        after_image_url: media.url,
      });
    } else if (mediaTarget === "gallery") {
      setFormData({
        ...formData,
        gallery_image_ids: [...formData.gallery_image_ids, media.media_id],
        gallery_urls: [...formData.gallery_urls, { id: media.media_id, url: media.url }],
      });
    }
    setMediaOpen(false);
  };

  const removeGalleryImage = (index) => {
    setFormData({
      ...formData,
      gallery_image_ids: formData.gallery_image_ids.filter((_, i) => i !== index),
      gallery_urls: formData.gallery_urls.filter((_, i) => i !== index),
    });
  };

  const addVideoUrl = () => {
    if (videoUrl && !formData.video_urls.includes(videoUrl)) {
      setFormData({
        ...formData,
        video_urls: [...formData.video_urls, videoUrl],
      });
      setVideoUrl("");
    }
  };

  const removeVideoUrl = (index) => {
    setFormData({
      ...formData,
      video_urls: formData.video_urls.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error("El título es requerido");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        slug: formData.slug || undefined,
        excerpt: formData.excerpt || undefined,
        content_html: formData.content_html || undefined,
        category: formData.category,
        year_approx: formData.year_approx || undefined,
        location_scope: formData.location_scope,
        municipio: formData.municipio || undefined,
        featured_image_id: formData.featured_image_id || undefined,
        before_image_id: formData.before_image_id || undefined,
        after_image_id: formData.after_image_id || undefined,
        gallery_image_ids: formData.gallery_image_ids,
        video_urls: formData.video_urls,
        author_name: formData.author_name || undefined,
        status: formData.status,
      };

      if (isEdit) {
        await axios.put(`${API}/memories/${memoryId}`, payload, {
        });
        toast.success("Memoria actualizada");
      } else {
        await axios.post(`${API}/memories`, payload);
        toast.success("Memoria creada");
      }
      navigate("/admin/memorias");
    } catch (error) {
      toast.error("Error al guardar");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="memory-edit-form">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/admin/memorias")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <h1 className="font-heading text-2xl font-bold text-brand-text">
              {isEdit ? "Editar Memoria" : "Nueva Memoria"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Borrador</SelectItem>
                <SelectItem value="published">Publicado</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Contenido</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Título de la memoria"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="year_approx">Año aproximado</Label>
                    <Input
                      id="year_approx"
                      value={formData.year_approx}
                      onChange={(e) => setFormData({ ...formData, year_approx: e.target.value })}
                      placeholder="Ej: 1985 o Década de los 70"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="author_name">Autor</Label>
                    <Input
                      id="author_name"
                      value={formData.author_name}
                      onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                      placeholder="Nombre del autor"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="excerpt">Extracto</Label>
                  <Textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="Breve descripción"
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="content">Contenido (HTML)</Label>
                  <Textarea
                    id="content"
                    value={formData.content_html}
                    onChange={(e) => setFormData({ ...formData, content_html: e.target.value })}
                    placeholder="<p>Historia completa...</p>"
                    rows={10}
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Before/After Images */}
            <Card>
              <CardHeader>
                <CardTitle>Comparativa Antes / Después</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Imagen ANTES</Label>
                    {formData.before_image_url ? (
                      <div className="relative mt-2">
                        <img
                          src={formData.before_image_url}
                          alt="Antes"
                          className="w-full aspect-video object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="absolute bottom-2 right-2"
                          onClick={() => handleMediaOpen("before")}
                        >
                          Cambiar
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-32 mt-2 flex-col gap-2"
                        onClick={() => handleMediaOpen("before")}
                      >
                        <ImageIcon className="h-8 w-8 text-gray-400" />
                        Seleccionar
                      </Button>
                    )}
                  </div>
                  <div>
                    <Label>Imagen DESPUÉS</Label>
                    {formData.after_image_url ? (
                      <div className="relative mt-2">
                        <img
                          src={formData.after_image_url}
                          alt="Después"
                          className="w-full aspect-video object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="absolute bottom-2 right-2"
                          onClick={() => handleMediaOpen("after")}
                        >
                          Cambiar
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-32 mt-2 flex-col gap-2"
                        onClick={() => handleMediaOpen("after")}
                      >
                        <ImageIcon className="h-8 w-8 text-gray-400" />
                        Seleccionar
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gallery */}
            <Card>
              <CardHeader>
                <CardTitle>Galería de Fotos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-3">
                  {formData.gallery_urls.map((img, idx) => (
                    <div key={idx} className="relative aspect-square">
                      <img
                        src={img.url}
                        alt={`Galería ${idx + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(idx)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-sm flex items-center justify-center"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="aspect-square flex-col gap-2"
                    onClick={() => handleMediaOpen("gallery")}
                  >
                    <Plus className="h-6 w-6" />
                    Agregar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Videos */}
            <Card>
              <CardHeader>
                <CardTitle>Videos de YouTube</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="flex-1"
                  />
                  <Button type="button" onClick={addVideoUrl}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.video_urls.length > 0 && (
                  <div className="space-y-2">
                    {formData.video_urls.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <Video className="h-4 w-4 text-gray-500" />
                        <span className="flex-1 text-sm truncate">{url}</span>
                        <button
                          type="button"
                          onClick={() => removeVideoUrl(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Category */}
            <Card>
              <CardHeader>
                <CardTitle>Categoría</CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Historia general</SelectItem>
                    <SelectItem value="antes-despues">Antes / Después</SelectItem>
                    <SelectItem value="tradicion">Tradición</SelectItem>
                    <SelectItem value="personaje">Personaje</SelectItem>
                    <SelectItem value="oficio">Oficio</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Featured Image */}
            <Card>
              <CardHeader>
                <CardTitle>Imagen Destacada</CardTitle>
              </CardHeader>
              <CardContent>
                {formData.featured_image_url ? (
                  <div className="relative">
                    <img
                      src={formData.featured_image_url}
                      alt="Featured"
                      className="w-full aspect-video object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="absolute bottom-2 right-2"
                      onClick={() => handleMediaOpen("featured")}
                    >
                      Cambiar
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-32 flex-col gap-2"
                    onClick={() => handleMediaOpen("featured")}
                  >
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                    Seleccionar imagen
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Location */}
            <Card>
              <CardHeader>
                <CardTitle>Ubicación</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Ámbito</Label>
                  <Select
                    value={formData.location_scope}
                    onValueChange={(value) => setFormData({ ...formData, location_scope: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="choluteca">Choluteca</SelectItem>
                      <SelectItem value="honduras">Honduras</SelectItem>
                      <SelectItem value="ambos">Ambos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="municipio">Municipio</Label>
                  <Input
                    id="municipio"
                    value={formData.municipio}
                    onChange={(e) => setFormData({ ...formData, municipio: e.target.value })}
                    placeholder="Ej: Choluteca"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>

      {/* Media Dialog */}
      <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Seleccionar Imagen</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mt-4">
            {mediaList.map((media) => (
              <button
                key={media.media_id}
                type="button"
                onClick={() => handleMediaSelect(media)}
                className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-colors"
              >
                <img
                  src={media.url}
                  alt={media.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
            {mediaList.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                No hay imágenes. Sube algunas en Media.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
